/**
 * Terminology — Centralized UI label resolver for location hierarchy.
 *
 * WHY: The backend uses "campus" everywhere (tables, DTOs, API contracts).
 *      The UI decouples display labels from those identifiers so we can
 *      show "Facility", "Site", etc. per-tenant without touching dozens of files.
 *
 * THREE-LAYER DESIGN:
 *   Layer 1 — Pure resolver functions (this file).
 *             Accept an optional TerminologyConfig; no React, no side effects.
 *   Layer 2 — React hook (useTerminology.ts).
 *             Reads tenant context from AuthContext, returns a TerminologySet.
 *   Layer 3 — Factory (buildTerminology in this file).
 *             For non-React modules: accepts config, returns a frozen TerminologySet.
 *
 * RESOLUTION POLICY (this slice):
 *   1. verticalPackCode === 'uae_education' → Campus
 *   2. industryCode === 'education'         → Campus
 *   3. Anything else                        → Facility
 *   4. Fallback (null/undefined/omitted)    → Campus
 *
 * BACKWARD COMPAT:
 *   All existing bare function calls (no config arg) return "Campus".
 *   Call sites must migrate to useTerminology() or buildTerminology() to
 *   become tenant-aware.
 */

// ── Types ────────────────────────────────────────────────────────────────────

/** Input for terminology resolution — sourced from tenant business profile. */
export interface TerminologyConfig {
  industryCode?: string | null;
  verticalPackCode?: string | null;
}

/** All resolved location labels as plain strings. */
export interface TerminologySet {
  locationLabel: string;
  locationLabelPlural: string;
  locationLabelAdd: string;
  locationLabelEdit: string;
  locationLabelDelete: string;
  locationLabelAll: string;
  locationLabelScope: string;
  locationLabelCode: string;
  locationLabelDetails: string;
  locationSearchPlaceholder: string;
  locationManageSubtitle: string;
}

// ── Core resolution ──────────────────────────────────────────────────────────

function isEducation(config?: TerminologyConfig): boolean {
  if (!config) return true; // fallback = education vocabulary
  if (config.verticalPackCode === 'uae_education') return true;
  if (config.industryCode === 'education') return true;
  // null/undefined fields with no decisive signal → fallback to education
  if (!config.industryCode && !config.verticalPackCode) return true;
  return false;
}

// ── Core labels ──────────────────────────────────────────────────────────────

/** Singular label for the top-level location entity. */
export function locationLabel(config?: TerminologyConfig): string {
  return isEducation(config) ? 'Campus' : 'Facility';
}

/** Plural label for the top-level location entity. */
export function locationLabelPlural(config?: TerminologyConfig): string {
  return isEducation(config) ? 'Campuses' : 'Facilities';
}

// ── Derived / contextual labels ──────────────────────────────────────────────

/** "Add Campus", "Add Facility", etc. */
export function locationLabelAdd(config?: TerminologyConfig): string {
  return `Add ${locationLabel(config)}`;
}

/** "Edit Campus", "Edit Facility", etc. */
export function locationLabelEdit(config?: TerminologyConfig): string {
  return `Edit ${locationLabel(config)}`;
}

/** "Delete Campus", etc. */
export function locationLabelDelete(config?: TerminologyConfig): string {
  return `Delete ${locationLabel(config)}`;
}

/** "All Campuses", "All Facilities", etc. */
export function locationLabelAll(config?: TerminologyConfig): string {
  return `All ${locationLabelPlural(config)}`;
}

/** "Campus Scope", "Facility Scope", etc. */
export function locationLabelScope(config?: TerminologyConfig): string {
  return `${locationLabel(config)} Scope`;
}

/** "Campus Code", "Facility Code", etc. */
export function locationLabelCode(config?: TerminologyConfig): string {
  return `${locationLabel(config)} Code`;
}

/** "Campus Details", "Facility Details", etc. */
export function locationLabelDetails(config?: TerminologyConfig): string {
  return `${locationLabel(config)} Details`;
}

/** Search placeholder: "Search campuses...", etc. */
export function locationSearchPlaceholder(config?: TerminologyConfig): string {
  return `Search ${locationLabelPlural(config).toLowerCase()}...`;
}

/** Page subtitle: "Manage your organization campuses", etc. */
export function locationManageSubtitle(config?: TerminologyConfig): string {
  return `Manage your organization ${locationLabelPlural(config).toLowerCase()}`;
}

// ── Factory (Layer 3) ────────────────────────────────────────────────────────

/**
 * Build a frozen TerminologySet from a config.
 * Use in non-React modules where a hook cannot be called.
 */
export function buildTerminology(config: TerminologyConfig): TerminologySet {
  return Object.freeze({
    locationLabel: locationLabel(config),
    locationLabelPlural: locationLabelPlural(config),
    locationLabelAdd: locationLabelAdd(config),
    locationLabelEdit: locationLabelEdit(config),
    locationLabelDelete: locationLabelDelete(config),
    locationLabelAll: locationLabelAll(config),
    locationLabelScope: locationLabelScope(config),
    locationLabelCode: locationLabelCode(config),
    locationLabelDetails: locationLabelDetails(config),
    locationSearchPlaceholder: locationSearchPlaceholder(config),
    locationManageSubtitle: locationManageSubtitle(config),
  });
}
